import { APIEntityType, ArdoqId } from '@ardoq/api-types';
import {
  ComponentDataSourceItem,
  ComponentItemData,
  PrepareDataSource,
} from '../types';
import { WILDCARD, isTruthy } from '@ardoq/common-helpers';

export const getDataSource = (
  collapsed: Record<ArdoqId, boolean>,
  hierarchy: Record<ArdoqId, { isFilteredOut: boolean; children?: ArdoqId[] }>,
  itemDataMap: Record<ArdoqId, ComponentItemData>,
  limit = Infinity
): ComponentDataSourceItem[] =>
  composeSourceData({
    hierarchy,
    collapsed,
    children: hierarchy.root.children || [],
    itemDataMap,
    limit,
  }).filter(isTruthy);

const composeSourceData = ({
  hierarchy,
  collapsed,
  children,
  depth = 0,
  itemDataMap,
  isParentCollapsed,
  limit = Infinity,
}: PrepareDataSource): (ComponentDataSourceItem | null)[] => {
  let remainingLimit = limit;

  return children.reduce<(ComponentDataSourceItem | null)[]>((acc, id) => {
    const isCollapsed = collapsed[WILDCARD] || Boolean(collapsed[id]);

    if (!hierarchy[id] || remainingLimit <= 0) {
      return acc;
    }

    const { isFilteredOut, children: itemChildren = [] } = hierarchy[id];

    const hasChildren = Boolean(itemChildren && itemChildren.length > 0);

    const data = {
      id,
      depth,
      isCollapsed,
      hasChildren,
      itemData: itemDataMap[id],
      isFilteredOut,
      isHovered: false,
      isFocused: false,
      isSelected: false,
    };

    acc.push(isParentCollapsed ? null : data);
    if (data.itemData.type !== APIEntityType.WORKSPACE) {
      remainingLimit--;
    }

    if (hasChildren) {
      const childItemsData = composeSourceData({
        hierarchy,
        collapsed,
        children: itemChildren,
        depth: depth + 1,
        itemDataMap,
        isParentCollapsed: isCollapsed || isParentCollapsed,
        limit: remainingLimit,
      });

      childItemsData.forEach(childItem => {
        if (remainingLimit > 0) {
          acc.push(childItem);
          remainingLimit--;
        }
      });
    }
    return acc;
  }, []);
};
